import { Building2, Mail, ShieldCheck, UserRound, WalletCards } from "lucide-react";

import { useAuth } from "@/app/providers/AuthProvider";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { Loader } from "@/components/common/Loader";
import { PageHeader } from "@/components/ui/page-header";
import { accountLabel } from "@/features/accounts/accountLabel";
import { useAccounts } from "@/features/accounts/hooks/useAccounts";
import { useCurrentOrganization } from "@/features/organization/hooks/useOrganization";
import { formatMoney } from "@/lib/format";

export default function ProfilePage() {
  const { user } = useAuth();
  const { organization, isLoading: organizationLoading, error: organizationError } = useCurrentOrganization();
  const { accounts, isLoading: accountsLoading, error: accountsError, refresh } = useAccounts();

  if (organizationLoading || accountsLoading) return <Loader />;

  if (accountsError) {
    return <ErrorState message={accountsError} onRetry={() => refresh()} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Mon profil" subtitle="Vos informations et les comptes de votre organisation." />

      <section
        aria-labelledby="profile-details-title"
        className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]"
      >
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="grid h-12 w-12 place-items-center rounded-full bg-brand text-lg font-semibold text-brand-black"
            >
              {(user?.username ?? "U").slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0">
              <h2 id="profile-details-title" className="text-lg font-semibold">
                {user?.username ?? "Utilisateur"}
              </h2>
              <p className="text-sm text-muted-foreground">Profil utilisateur</p>
            </div>
          </div>

          <dl className="mt-6 divide-y divide-border">
            <ProfileDetail
              icon={<Building2 size={17} />}
              label="Organisation"
              value={organization?.name ?? "Organisation inconnue"}
            />
            <ProfileDetail icon={<Mail size={17} />} label="Adresse e-mail" value={user?.email || "Non renseignée"} />
            <ProfileDetail
              icon={<ShieldCheck size={17} />}
              label="Rôle(s)"
              value={user?.roles?.length ? user.roles.join(", ") : "Aucun rôle renseigné"}
            />
          </dl>

          {organizationError ? (
            <p className="mt-4 text-sm text-muted-foreground">Les informations de l’organisation sont indisponibles.</p>
          ) : null}
        </div>

        <section aria-labelledby="accounts-title" className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 id="accounts-title" className="text-lg font-semibold">
                Comptes
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">Soldes disponibles pour votre organisation.</p>
            </div>
            <WalletCards className="shrink-0 text-muted-foreground" aria-hidden size={21} />
          </div>

          {accounts.length ? (
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {accounts.map((account, index) => (
                <article key={account.id} className="rounded-lg border border-border bg-muted/40 p-4">
                  <p className="text-sm font-medium text-muted-foreground">{accountLabel(account, index)}</p>
                  <p className="mt-2 text-2xl font-semibold tabular-nums">
                    {formatMoney(account.balance, account.currency)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">Solde actuel</p>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-5">
              <EmptyState title="Aucun compte disponible" hint="Les comptes de votre organisation apparaîtront ici." />
            </div>
          )}
        </section>
      </section>
    </div>
  );
}

function ProfileDetail({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
      <span className="mt-0.5 text-muted-foreground" aria-hidden>
        {icon}
      </span>
      <div className="min-w-0">
        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
        <dd className="mt-1 break-words text-sm font-medium">{value}</dd>
      </div>
    </div>
  );
}
